import { http } from '@/lib/axios';
import { unwrap, toApiError } from '@/lib/unwrap';
import type { ApiEnvelope } from '@/types/api';
import type {
  AuthUser,
  LoginCodeRequested,
  LoginMethods,
  LoginResponse,
  MePermissions,
} from '@/types/auth';

export async function login(email: string, password: string): Promise<LoginResponse> {
  try {
    const res = await http.post<ApiEnvelope<LoginResponse>>('/auth/login', { email, password });
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * Which sign-in methods the server accepts right now.
 *
 * Asked rather than assumed: the mode is an operator setting, so hardcoding it
 * here would mean this screen and the server could disagree — the cashier would
 * see a control that 404s, or miss the only one that works.
 *
 * A failure resolves to password-only. That is the safe direction: the password
 * form is what every deployment has had until now.
 */
export async function fetchLoginMethods(): Promise<LoginMethods> {
  try {
    const res = await http.get<ApiEnvelope<LoginMethods>>('/auth/login/methods');
    const data = unwrap(res.data);
    return { password: data?.password !== false, otp: data?.otp === true };
  } catch {
    return { password: true, otp: false };
  }
}

/** Ask for a one-time code by email. The response never carries the code. */
export async function requestLoginCode(email: string): Promise<LoginCodeRequested> {
  try {
    const res = await http.post<ApiEnvelope<LoginCodeRequested>>('/auth/login/otp/request', {
      email,
    });
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * Exchange a code for a session. The envelope is identical to `login()` — same
 * user, tokens and session keys — so everything downstream is unchanged.
 */
export async function verifyLoginCode(email: string, code: string): Promise<LoginResponse> {
  try {
    const res = await http.post<ApiEnvelope<LoginResponse>>('/auth/login/otp/verify', {
      email,
      code,
    });
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * Server-side session revoke. MUST be awaited BEFORE local state is cleared.
 *
 * Measured on production 2026-10-04: sign-out cleared localStorage and sent no
 * HTTP request at all, so the access token stayed valid for its remaining ~15
 * minutes and the REFRESH token for 7 days, continuing to mint new access
 * tokens. On a shared POS or handheld the previous operator's session outlives
 * their shift.
 *
 * The backend is correct and was simply being called wrong:
 *   POST /auth/logout WITH    bearer -> 200, token then 401
 *   POST /auth/logout WITHOUT bearer -> 401, token stays LIVE
 *
 * So the ordering matters more than the call: the request interceptor reads the
 * token from the store on every request, so clearing the store first makes the
 * revoke go out unauthenticated and silently fail. Await this, then log out.
 *
 * Errors are swallowed on purpose — a network failure must not trap an operator
 * in a session they asked to leave. Local logout always proceeds.
 */
export async function logoutSession(
  refreshToken?: string | null,
  /**
   * Bearer to revoke with when the token is NOT in the store yet — a login the
   * POS refused (no POS permission) must still kill the session it just minted.
   */
  accessToken?: string | null,
): Promise<void> {
  try {
    await http.post(
      '/auth/logout',
      refreshToken ? { refreshToken } : {},
      accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined,
    );
  } catch {
    // 401 / already-revoked / offline — local logout still proceeds.
  }
}

export async function fetchMe(): Promise<AuthUser> {
  try {
    const res = await http.get<ApiEnvelope<AuthUser>>('/me');
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * GET /me/permissions — the caller's effective RBAC grants. `accessToken` is
 * passed explicitly during login, before the session is in the store (the
 * request interceptor leaves an explicit Authorization header alone).
 */
export async function fetchMyPermissions(accessToken?: string | null): Promise<MePermissions> {
  try {
    const res = await http.get<ApiEnvelope<Partial<MePermissions>>>(
      '/me/permissions',
      accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined,
    );
    const data = unwrap(res.data) ?? {};
    return {
      ...data,
      permissions: Array.isArray(data.permissions) ? data.permissions.map(String) : [],
      wildcards: Array.isArray(data.wildcards) ? data.wildcards.map(String) : [],
    };
  } catch (error) {
    throw toApiError(error);
  }
}
